/**
 * EventDateEditModal — the ONE surface for editing a saved race's date
 * (role-conflict robustness spec §6.1, build-plan F1). Custom `CustomerEvent`
 * rows only; catalog-linked dates are shared rows the server 422s
 * (`catalog_event_date_immutable`) — entry points gate the affordance, this
 * modal surfaces the server copy verbatim as the backstop.
 *
 * Flow: small date editor → `PATCH /api/events/:id`. A `409
 * event_date_collision` (moving the date into another build's window) opens
 * the shared `RoleResolutionSheet` as a `pair`, exactly the way the create
 * funnel does (useEventSetup §6.3): each pick accumulates
 * `{ journeyId, mode }` and the retry carries the WHOLE array — nothing is
 * partially committed, a cancel at any round abandons them all. `nest_new` is
 * NOT a valid retry mode on this endpoint, so it's filtered from the offered
 * outcomes. Defensive cap of 6 sheet rounds mirrors the funnel (never
 * reintroduce the unbounded 409 loop B1 fixed).
 *
 * Deliberately generic — no context/singleton coupling — so the sheet's
 * "Edit the date instead" escape hatch (a later stitch) can open it directly.
 */
import { useState } from "react";
import { ModalShell } from "../modals/ModalShell";
import { DateField } from "../ui/DateField";
import { useAuth } from "../../context/AuthProvider";
import { useToast } from "../../context/ToastProvider";
import { userFacingError } from "../../lib/api";
import { compareYmd, formatYmdLong } from "../../lib/dates";
import {
  eventDateCollisionFromError,
  patchEventDate,
  type EventDTO,
  type EventDateCollision,
  type EventDateOverlapResolution
} from "../../lib/eventsApi";
import { notifyJourneyChanged, type EventRef } from "../../lib/journeyApi";
import {
  RoleResolutionSheet,
  type RoleResolutionChoice,
  type RoleResolutionContext
} from "./RoleResolutionSheet";
import "./planner.css";

export type EventDateEditModalEvent = {
  id: string;
  name: string;
  /** Current race day, `YYYY-MM-DD` (may be "" defensively — Save requires one). */
  dateYmd: string;
  /** Null/absent ⇒ custom (editable). Carried for parity with the DTO; entry
   *  points gate the affordance, the server 422 is the trust boundary. */
  eventCatalogId?: string | null;
};

/** Defensive cap on sheet rounds — mirrors `useEventSetup.createRaceJourney`. */
const MAX_SHEET_ROUNDS = 6;

export function EventDateEditModal({
  event,
  onSaved,
  onClose
}: {
  event: EventDateEditModalEvent;
  /** Fires with the server's updated event AFTER the success toast, right
   *  before `onClose` — hosts refresh their events list here. */
  onSaved: (event: EventDTO) => void;
  onClose: () => void;
}) {
  const { state: authState, refreshCsrf } = useAuth();
  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon"
      ? authState.csrfToken
      : null;
  const { push: pushToast } = useToast();

  const [dateYmd, setDateYmd] = useState(event.dateYmd);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The open 409 round (null = editor showing) + the picks accumulated so far.
  const [collision, setCollision] = useState<EventDateCollision | null>(null);
  const [resolved, setResolved] = useState<EventDateOverlapResolution[]>([]);

  /** One PATCH attempt carrying the accumulated resolutions. Success closes;
   *  a further 409 opens the next sheet round; anything else lands inline. */
  async function saveWith(resolutions: EventDateOverlapResolution[]): Promise<void> {
    if (!csrfToken) {
      setError("Sign in to change this race's date.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await patchEventDate(
        event.id,
        {
          eventDate: dateYmd,
          ...(resolutions.length > 0 ? { overlapResolutions: resolutions } : {})
        },
        csrfToken,
        refreshCsrf
      );
      notifyJourneyChanged();
      pushToast({
        tone: "success",
        message: "Race date updated — your training plan and fuel boxes follow it."
      });
      onSaved(res.event);
      onClose();
      return;
    } catch (err) {
      const col = eventDateCollisionFromError(err);
      if (col) {
        // nest_new is a create-time outcome, not a retry mode this endpoint
        // accepts — never offer it as a card here.
        const offered = col.resolutions.filter((o) => o !== "nest_new");
        if (offered.length === 0 || resolutions.length >= MAX_SHEET_ROUNDS) {
          setResolved([]);
          setError("We couldn't resolve the race overlaps — please try again.");
        } else {
          setCollision({ ...col, resolutions: offered });
        }
      } else {
        setError(userFacingError(err, "Couldn't update the date — try again."));
      }
      setSaving(false);
    }
  }

  function handleSave(): void {
    if (saving) return;
    if (!dateYmd) {
      setError("Pick a date first.");
      return;
    }
    // Same date → nothing to do; just close (no PATCH).
    if (dateYmd === event.dateYmd) {
      onClose();
      return;
    }
    setResolved([]);
    void saveWith([]);
  }

  /** Build the pair sheet context from the 409 — the funnel's construction
   *  (`pairContextFromConflict`): moved event + existing, anchor = later ymd,
   *  server `resolutions` authoritative, §6.3 counter when N > 1. */
  function sheetContext(col: EventDateCollision): RoleResolutionContext {
    const moved: EventRef = {
      id: col.newEvent?.id ?? event.id,
      name: col.newEvent?.name ?? event.name,
      ymd: col.newEvent?.ymd ?? dateYmd,
      hasJourney: false
    };
    const existing: EventRef = {
      id: col.existing.journeyId,
      name: col.existing.raceName ?? "your current race",
      ymd: col.existing.raceYmd ?? "",
      hasJourney: col.existing.hasJourney ?? true
    };
    const movedIsLater = !!existing.ymd && compareYmd(moved.ymd, existing.ymd) > 0;
    return {
      kind: "pair",
      events: [moved, existing],
      anchor: movedIsLater ? moved : existing,
      outcomes: col.resolutions,
      // 1-based round number; the server's conflictCount is what REMAINS at
      // this 409, so the known total = already-resolved + remaining.
      conflictIndex: resolved.length + 1,
      conflictCount: resolved.length + (col.conflictCount ?? 1),
      consequences: col.consequences ?? undefined
    };
  }

  function onSheetResolve(choices: RoleResolutionChoice[]): void {
    const choice = choices[0];
    if (!collision || !choice || choice.kind !== "pair") {
      onSheetCancel();
      return;
    }
    const mode: EventDateOverlapResolution["mode"] | null =
      choice.outcome === "nest_existing"
        ? "demote"
        : choice.outcome === "chain"
          ? "chain"
          : null;
    if (!mode) {
      // nest_new can't reach here (filtered) — treat defensively as a cancel.
      onSheetCancel();
      return;
    }
    const next = [...resolved, { journeyId: collision.existing.journeyId, mode }];
    setResolved(next);
    setCollision(null);
    void saveWith(next);
  }

  /** Sheet cancel → back to the date editor with NO change: the accumulated
   *  resolutions are abandoned (they only ever ride the PATCH request). */
  function onSheetCancel(): void {
    setCollision(null);
    setResolved([]);
    setSaving(false);
  }

  return (
    <>
      <ModalShell
        eyebrow="Race date"
        title="Change race date"
        onClose={onClose}
        width={420}
        footer={
          <>
            <button
              type="button"
              className="fg-btn fg-btn--ghost fg-btn--sm"
              onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="fg-btn fg-btn--primary fg-btn--sm"
              disabled={saving}
              onClick={handleSave}>
              {saving ? "Saving…" : "Save date"}
            </button>
          </>
        }>
        <div className="fgp-modalshell__pad">
          <p className="fgp-window-caption">
            <strong>{event.name}</strong>
            {event.dateYmd ? ` is currently on ${formatYmdLong(event.dateYmd)}.` : "."} Your
            build and fuel boxes follow the new date.
          </p>
          <div className="fgp-dateedit-field">
            <span className="fg-eyebrow">New race date</span>
            <DateField
              value={dateYmd}
              aria-label={`Race date for ${event.name}`}
              showLongFormat
              onChange={(ymd) => {
                setDateYmd(ymd);
                setError(null);
              }}
            />
          </div>
          {error && (
            <div className="fgp-modal-callout fgp-modal-callout--error" role="alert">
              {error}
            </div>
          )}
        </div>
      </ModalShell>
      {/* The ONE role-decision surface, stacked over the editor when the new
          date collides with another build (same precedent as settings' C3). */}
      {collision && (
        <RoleResolutionSheet
          context={sheetContext(collision)}
          onResolve={onSheetResolve}
          onCancel={onSheetCancel}
        />
      )}
    </>
  );
}
